
import integrate from "../../assets/integrate.svg";
import match from "../../assets/match.svg";
import secure from "../../assets/secure.svg";
import SyncBanner from "../../assets/sync-playlists.png";
import SyncRunBanner from "../../assets/sync-run-banner.png";

import config from "../../config";


import { OptimizeSEO } from "../../components/OptimizeSEO";
import { seoContent } from "../../constants/SEOConstant";
import { SUPPORTED_APPS } from "../../constants/HomePageConstants";
import { Link } from "react-router-dom";

function ViewSyncRun() {
  const features = [
    {
      name: "Sync Run History: ",
      description: "View detailed logs of all sync operations, including timestamps, success rates, and any errors encountered during synchronization.",
    },
    {
      name: "Real-time Monitoring: ",
      description: "Monitor active sync operations in real-time with live status updates and progress indicators.",
    },
    {
      name: "Daily Automatic Sync: ",
      description: "Your playlists are automatically synchronized daily to ensure they stay up-to-date across all platforms.",
    },
  ];

  const comingSoonFeatures = [
    {
      name: "Manual Override: ",
      description: "Take control when needed—manually approve, reject, or modify sync changes before they're applied to your playlists.",
    },
    {
      name: "Conflict Management: ",
      description: "Review and resolve sync conflicts with an intuitive interface that shows exactly what changes are being made.",
    },
    {
      name: "Custom Sync Scheduling: ",
      description: "Set custom sync schedules and frequency preferences to optimize when and how often your playlists are synchronized.",
    },
  ];

  return (
    <>
      <div className="max-w-screen-xl px-4 py-8 mx-auto space-y-12 lg:space-y-20 lg:py-24 lg:px-6">
        {/* Row */}
        <div className="items-center gap-8 lg:grid lg:grid-cols-2 xl:gap-16">
          <div className="text-gray-500 sm:text-lg dark:text-gray-400">
            <div className="lg:row-start-2 lg:max-w-md">
              <h2 className="text-3xl font-semibold tracking-tight text-balance text-gray-900 dark:text-white sm:text-4xl">
                Monitor & Control Your Sync
              </h2>
              <p className="mt-6 text-lg/8 text-gray-500 sm:text-xl dark:text-gray-400">
                Stay in complete control of your playlist synchronization with comprehensive monitoring tools and detailed analytics.
              </p>
            </div>

            <div className="pt-4 max-w-xl lg:row-start-3 lg:mt-10 lg:max-w-md lg:border-t lg:border-white/10 lg:pt-10">
              <dl className="max-w-xl space-y-6 text-base/7 text-gray-300 lg:max-w-none">
                {features.map((feature) => (
                  <div key={feature.name} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                    <div>
                      <dt className="font-semibold text-gray-900 dark:text-white mb-1">
                        {feature.name}
                      </dt>
                      <dd className="text-gray-500 dark:text-gray-400">
                        {feature.description}
                      </dd>
                    </div>
                  </div>
                ))}
              </dl>
              
              {/* Coming Soon Features */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                  Coming Soon
                </h4>
                <dl className="max-w-xl space-y-4 text-base/7 text-gray-300 lg:max-w-none">
                  {comingSoonFeatures.map((feature) => (
                    <div key={feature.name} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-gray-400 rounded-full mt-2"></div>
                      <div>
                        <dt className="font-semibold text-gray-600 dark:text-gray-400 mb-1">
                          {feature.name}
                        </dt>
                        <dd className="text-gray-400 dark:text-gray-500">
                          {feature.description}
                        </dd>
                      </div>
                    </div>
                  ))}
                </dl>
              </div>
              
              {/* Additional info */}
              <div className="mt-8 p-4 bg-purple-100 dark:bg-purple-900 rounded-lg border border-purple-200 dark:border-purple-800">
                <h4 className="font-semibold text-purple-900 dark:text-white mb-2">
                  💡 Pro Tip
                </h4>
                <p className="text-purple-800 dark:text-white text-sm">
                  Your playlists are automatically synced daily to keep them fresh across all platforms. Check the sync dashboard to monitor recent activity.
                </p>
              </div>
            </div>
          </div>
          <img
            className="pt-4 w-full mb-4 rounded-lg lg:mb-0 lg:flex shadow-lg"
            src={SyncRunBanner}
            alt="Sync monitoring dashboard and analytics interface"
          />
        </div>
      </div>
    </>
  );
}

function SyncFeature() {
  const features = [
    {
      name: "Bidirectional Sync: ",
      description: "Sync changes in both directions—additions and deletions are automatically reflected across all connected platforms.",
    },
    {
      name: "Smart Track Matching: ",
      description: "Our advanced algorithm finds the exact same tracks across different platforms, even when titles or artists are slightly different.",
    },
    {
      name: "Conflict Resolution: ",
      description: "When conflicts arise, our system intelligently resolves them based on your preferences and sync history.",
    },
    {
      name: "Real-time Monitoring: ",
      description: "Your playlists are continuously monitored for changes, ensuring updates happen within minutes of any modifications.",
    },
    {
      name: "Multiple Platform Support: ",
      description: "Sync between Spotify, Apple Music, YouTube Music, YouTube, and Tidal—all from one dashboard with seamless cross-platform compatibility.",
    },
    {
      name: "Sync History & Analytics: ",
      description: "Track all sync activities, view detailed logs, and monitor sync performance with comprehensive analytics.",
    },
  ];

  return (
    <>
      <div className="max-w-screen-xl px-4 py-8 mx-auto space-y-12 lg:space-y-20 lg:py-24 lg:px-6">
        {/* Row */}
        <div className="items-center gap-8 lg:grid lg:grid-cols-2 xl:gap-16">
          <div className="text-gray-500 sm:text-lg dark:text-gray-400">
            <div className="lg:row-start-2 lg:max-w-md">
              <h2 className="text-3xl font-semibold tracking-tight text-balance text-gray-900 dark:text-white sm:text-4xl">
                Advanced Sync Features
              </h2>
              <p className="mt-6 text-lg/8 text-gray-500 sm:text-xl dark:text-gray-400">
                Our sync technology goes beyond simple transfers. It creates intelligent, persistent connections between your playlists that adapt and learn from your music habits.
              </p>
            </div>

            <div className="pt-4 max-w-xl lg:row-start-3 lg:mt-10 lg:max-w-md lg:border-t lg:border-white/10 lg:pt-10">
              <dl className="max-w-xl space-y-6 text-base/7 text-gray-300 lg:max-w-none">
                {features.map((feature) => (
                  <div key={feature.name} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                    <div>
                      <dt className="font-semibold text-gray-900 dark:text-white mb-1">
                        {feature.name}
                      </dt>
                      <dd className="text-gray-500 dark:text-gray-400">
                        {feature.description}
                      </dd>
                    </div>
                  </div>
                ))}
              </dl>
              
              {/* Call to action */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Ready to experience seamless playlist synchronization?
                </p>
                <a
                  href={config.signupPath}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Start Syncing Now
                  <svg className="ml-2 -mr-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <img
            className="pt-4 w-full mb-4 rounded-lg lg:mb-0 lg:flex shadow-lg"
            src={SyncBanner}
            alt="Advanced playlist synchronization across music platforms"
          />
        </div>
      </div>
    </>
  );
}

export default function Sync() {
  return (
    <>
    <OptimizeSEO {...seoContent.sync} />

      <>
        {/* Hero Section */}
        <section className="bg-white dark:bg-zinc-950">
          <div className="max-w-screen-xl pt-20 px-4 py-8 mx-auto lg:py-24 lg:px-6 lg:pt-28">
            <div className="max-w-screen-md mx-auto mb-8 text-center lg:mb-12">
              <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white lg:text-5xl">
                Keep Your Playlists in Sync
              </h1>
              <p className="mb-8 font-light text-gray-500 sm:text-xl dark:text-gray-400">
                Never manually sync your playlists across platforms again. Our intelligent sync feature automatically keeps your music collections updated across Spotify, Apple Music, YouTube Music, YouTube, and Tidal.
              </p>
              
              {/* Key Benefits */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                <div className="text-center">
                                     <img src={integrate} alt="Seamless Integration" className="w-24 h-24 rounded-lg mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Seamless Integration</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Effortless connection across multiple music platforms</p>
                </div>
                
                <div className="text-center">
                                     <img src={match} alt="Smart Matching" className="w-24 h-24 rounded-lg mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Smart Matching</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Intelligent track matching across platforms</p>
                </div>
                
                <div className="text-center">
                                     <img src={secure} alt="Secure & Private" className="w-24 h-24 rounded-lg mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Secure & Private</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Your data stays private and secure</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* How It Works Section */}
        <section className="bg-gray-50 dark:bg-zinc-900">
          <div className="max-w-screen-xl px-4 py-16 mx-auto lg:py-24 lg:px-6">
            <div className="max-w-screen-md mx-auto text-center mb-12">
              <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                How Sync Works
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                Set up sync once and let our platform handle the rest. Here's how easy it is:
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">1</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Transfer Your Playlist</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Start by transferring your playlist to the target platform using our transfer feature. This creates the initial playlist that will be kept in sync.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">2</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Create Sync</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Once the transfer is complete, click "Create Sync" from your transfer history. This establishes a permanent connection between your playlists.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">3</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Automatic Updates</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Our system monitors your source playlist and automatically syncs any changes—new songs and removed tracks—to all connected platforms.
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-screen-xl px-4 py-8 mx-auto space-y-12 lg:space-y-20 lg:py-10 lg:px-6">
          <div className="mx-auto max-w-4xl">
          <p className="mt-2 text-2xl sm:text-3xl md:text-5xl text-center font-extrabold leading-tight tracking-tight text-balance text-gray-900 dark:text-white">
            Supported Platforms
          </p>
        </div>
            <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none sm:grid-cols-2 lg:grid-cols-3">
            {SUPPORTED_APPS.map(({ id, name, icon, link }) => (
              <Link to={link} key={id} className="card-link">
                <div key={id} className="card custom-card">
                  <h3 className="card-title text-gray-900 dark:text-white">{name}</h3>
                  <div className="card-icon">
                    <img src={icon} className="responsive-icon" alt={`transfer ${name} playlists`} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
          </div>

        
        </section>
      </>

      {}

      <section className="bg-white dark:bg-zinc-800">
        <SyncFeature></SyncFeature>
      </section>

      <section className="bg-gray-50 dark:bg-zinc-950">
        <ViewSyncRun></ViewSyncRun>
      </section>


      {/* Call to Action Section */}
      <section className="bg-gray-50 dark:bg-zinc-800">
        <div className="max-w-screen-xl px-4 py-16 mx-auto lg:py-24 lg:px-6">
          <div className="max-w-screen-md mx-auto text-center">
            <h2 className="mb-6 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white lg:text-4xl">
              Ready to Keep Your Playlists in Perfect Sync?
            </h2>
                            <p className="mb-8 font-light text-gray-500 dark:text-gray-400 md:text-lg">
              Join thousands of music lovers who never worry about manually transferring playlists again. 
              Start syncing your music collection across Spotify, Apple Music, YouTube Music, YouTube, and Tidal today.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href={config.signupPath}
                className="text-white bg-purple-700 hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-8 py-3 dark:bg-purple-600 dark:hover:bg-purple-700 focus:outline-none dark:focus:ring-purple-800 transition-colors duration-200"
              >
                Start Now
              </a>
              <a
                href="/transfer-playlists-and-songs"
                className="text-purple-700 bg-white border border-purple-700 hover:bg-purple-50 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-8 py-3 dark:text-purple-400 dark:border-purple-400 dark:hover:bg-purple-900/20 focus:outline-none dark:focus:ring-purple-800 transition-colors duration-200"
              >
                Learn About Transfer
              </a>
            </div>
            
            <div className="mt-8 text-sm text-gray-500 dark:text-gray-400">
              <p>✓ No credit card required • ✓ Free forever transfer • ✓ Lifetime plan available</p>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}
